import { FormGroup, ValidationErrors, Validators, AbstractControl, FormArray } from '@angular/forms';
import * as moment from 'moment';
import { Moment } from 'moment';
import { FieldType } from '../../../../settings/model/field-type';
import { ValueField } from '../../../../settings/model/value-field';
import { setFormGroupControlsErrors, floatRegExp, integerRegExp, timeRegExp } from '../../../util/validation-util';
import { CustomValidator } from '../../../model/custom-validator';
import { Operator } from '../../../model/operator';
import { isArray } from 'lodash';

export class FilterValidation {

  static readonly controlField = 'field';
  static readonly controlOperator = 'operator';
  static readonly controlValue = 'value';
  static readonly controlStart = 'start';
  static readonly controlEnd = 'end';
  static readonly controlStartToShow = 'startToShow';
  static readonly controlEndToShow = 'endToShow';
  static readonly controlsStartEnd = [FilterValidation.controlStart, FilterValidation.controlEnd];
  static readonly controlsStartEndToShow = [
    FilterValidation.controlStart, FilterValidation.controlEnd, FilterValidation.controlStartToShow, FilterValidation.controlEndToShow
  ];

  private form: FormGroup;
  private customValidators: CustomValidator[];

  constructor(form: FormGroup) {
    this.form = form;
  }

  get formCustomValidators(): CustomValidator[] {
    return this.customValidators;
  }

  setCustomValidators(customValidators: CustomValidator[] | null): void {
    if (customValidators) {
      this.customValidators = customValidators;
      this.form.setValidators(this.customValidator.bind(this));
      this.form.updateValueAndValidity();
    }
  }

  private customValidator(formControl: FormGroup): ValidationErrors | null {
    if (this.customValidators) {
      let error: ValidationErrors = null;
      const result = this.customValidators.some(validator => {
        const validatorResult = validator.validate(formControl);
        if (validatorResult != null) {
          error = validatorResult;
          return true;
        }
        return false;
      });

      if (result)
        return error;
    }
    return null;
  }

  private filterGroupValidator(formGroup: FormGroup): ValidationErrors | null {
    const valueField: ValueField = formGroup.get(FilterValidation.controlField).value;
    if (valueField) {
      const operator = formGroup.get(FilterValidation.controlOperator).value;
      const valueControl = formGroup.get(FilterValidation.controlValue);
      if (operator && (operator == Operator.isNull || operator == Operator.notNull)) {
        valueControl.setErrors(null);
        if (valueControl instanceof FormArray) {
          for (const nestedControl of valueControl.controls) {
            nestedControl.setErrors(null);
          }
        }
        return null;
      }

      switch (valueField.type) {
        case FieldType.BOOLEAN:
          return this.validateMultipleAndMarkError(valueControl, [{ validator: Validators.required }]);

        case FieldType.INTEGER:
          if (operator === Operator.between || operator === Operator.betweenInclusive) {
            this.clearFormArrayErrors(valueControl);
            return this.validateBetweenNumeric(formGroup, integerRegExp);
          }
          setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEnd);
          return this.validateMultipleAndMarkError(valueControl, [
            { validator: Validators.required },
            { validator: Validators.pattern(integerRegExp) }
          ]);

        case FieldType.FLOAT:
          if (operator === Operator.between || operator === Operator.betweenInclusive) {
            this.clearFormArrayErrors(valueControl);
            return this.validateBetweenNumeric(formGroup, floatRegExp);
          }
          setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEnd);
          return this.validateMultipleAndMarkError(valueControl, [
            { validator: Validators.required },
            { validator: Validators.pattern(floatRegExp) }
          ]);

        case FieldType.DATE:
          return this.validateDate(formGroup);

        case FieldType.DATETIME:
          return this.validateDateTime(formGroup);

        case FieldType.TIME:
          return this.validateTime(formGroup);

        default:
          return this.validateAndMarkError(valueControl, this.noWhitespaceValidator);
      }
    } else
      return null;
  }

  getFilterGroupValidator(): (formGroup: FormGroup) => ValidationErrors | null {
    return this.filterGroupValidator.bind(this);
  }

  private validateTime(formGroup: FormGroup): ValidationErrors | null {
    const startTimeResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlStart), Validators.pattern(timeRegExp));
    const endTimeResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlEnd), Validators.pattern(timeRegExp));
    if (startTimeResult == null && endTimeResult == null) {
      const startTime = moment(formGroup.get(FilterValidation.controlStart).value, ['H:m']);
      const endTime = moment(formGroup.get(FilterValidation.controlEnd).value, ['H:m']);
      if (startTime.isBefore(endTime)) {
        setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEnd);
        return null;
      }

      const timeOrderError = { time_order: true };
      setFormGroupControlsErrors(timeOrderError, formGroup, FilterValidation.controlsStartEnd);
      return timeOrderError;
    }
    return startTimeResult;
  }

  private dateNotEmptyValidator(control: AbstractControl): ValidationErrors | null {
    const v = control.value;
    if (v == null || v === '') return { required: true };
    return null;
  }

  private validateDate(formGroup: FormGroup): ValidationErrors | null {
    const operator = formGroup.get(FilterValidation.controlOperator).value as Operator;
    if (operator === Operator.equals) {
      const result = this.validateMultipleAndMarkError(formGroup.get(FilterValidation.controlValue), [{ validator: this.dateNotEmptyValidator }]);
      if (result == null)
        setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEndToShow);
      return result;
    }
    const startDateResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlStart), Validators.required);
    const endDateResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlEnd), Validators.required);
    formGroup.get(FilterValidation.controlStartToShow).setErrors(startDateResult);
    formGroup.get(FilterValidation.controlEndToShow).setErrors(endDateResult);
    if (startDateResult == null && endDateResult == null) {
      if ((formGroup.get(FilterValidation.controlStart).value as Moment).isSameOrBefore(formGroup.get(FilterValidation.controlEnd).value as Moment)) {
        setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEndToShow);
        return null;
      }
      const dateOrderError = { date_order: true };
      setFormGroupControlsErrors(dateOrderError, formGroup, FilterValidation.controlsStartEndToShow);
      return dateOrderError;
    }
    return startDateResult;
  }

  private clearFormArrayErrors(control: AbstractControl): void {
    control.setErrors(null);
    if (control instanceof FormArray)
      control.controls.forEach(ctrl => ctrl.setErrors(null));
  }

  private validateBetweenNumeric(formGroup: FormGroup, pattern: RegExp): ValidationErrors | null {
    const patternValidator = Validators.pattern(pattern);
    for (const control of [formGroup.get(FilterValidation.controlStart), formGroup.get(FilterValidation.controlEnd)]) {
      const requiredError = this.validateAndMarkError(control, Validators.required);
      if (requiredError) return requiredError;
      const patternError = this.validateAndMarkError(control, patternValidator);
      if (patternError) return patternError;
    }
    return null;
  }

  private validateDateTime(formGroup: FormGroup): ValidationErrors | null {
    const op = formGroup.get(FilterValidation.controlOperator).value as Operator;
    if (op === Operator.equals) {
      const result = this.validateMultipleAndMarkError(formGroup.get(FilterValidation.controlValue), [{ validator: this.dateNotEmptyValidator }]);
      if (result == null)
        setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEndToShow);
      return result;
    }
    const startDateResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlStart), Validators.required);
    const endDateResult = this.validateAndMarkError(formGroup.get(FilterValidation.controlEnd), Validators.required);
    formGroup.get(FilterValidation.controlStartToShow).setErrors(startDateResult);
    formGroup.get(FilterValidation.controlEndToShow).setErrors(endDateResult);
    if (startDateResult == null && endDateResult == null) {
      const start = formGroup.get(FilterValidation.controlStart).value as Moment;
      const end = formGroup.get(FilterValidation.controlEnd).value as Moment;
      let valid: boolean = true;
      switch (op) {
        case Operator.betweenInclusive:
          valid = start.isSameOrBefore(end);
          break;
       case Operator.between:
          valid = start.isBefore(end);
          break;
      }
      if (valid) {
        setFormGroupControlsErrors(null, formGroup, FilterValidation.controlsStartEndToShow);
        return null;
      }
      const dateOrderError = { date_order: true };
      setFormGroupControlsErrors(dateOrderError, formGroup, FilterValidation.controlsStartEndToShow);
      return dateOrderError;
    }
    return startDateResult;
  }

  private validateMultipleAndMarkError(control: AbstractControl, validations: ControlValidation[]): ValidationErrors | null {
    for (const validation of validations) {
      if (control instanceof FormArray) {
        let validatorResult = null;
        control.controls.forEach(innerControl => {
          const innerControlValidationResult = validation.validator(innerControl, validations);
          if (innerControlValidationResult) {
            validatorResult = innerControlValidationResult;
            innerControl.setErrors(innerControlValidationResult);
          }
        });
        if (validatorResult != null)
          return validatorResult;
      } else {
        const validatorResult = validation.validator(control, validation.param);
        control.setErrors(validatorResult);
        if (validatorResult != null)
          return validatorResult;
      }
    }
    return null;
  }

  private validateAndMarkError(control: AbstractControl, validator: (control: AbstractControl, param?: any) => ValidationErrors | null, validatorParam?: any):
    ValidationErrors | null {
    let validatorResult = validator(control, validatorParam);
    if (control instanceof FormArray) {
      control.controls.forEach(innerControl => {
        const innerControlValidationResult = this.validateAndMarkError(innerControl, validator);
        if (innerControlValidationResult)
          validatorResult = innerControlValidationResult;
      });
    } else {
      validatorResult = validator(control, validatorParam);
      control.setErrors(validatorResult);
    }
    return validatorResult;
  }

  private noWhitespaceValidator(formControl: AbstractControl): ValidationErrors | null {
    const value = formControl.value;
    if (value == null)
      return null;
    if (isArray(value)) {
      const res = value.some(element => {
        if (element != null && typeof element !== 'string')
          return false;
        const isWhitespace = (element || '').trim().length === 0;
        return isWhitespace;
      });
      if (res)
        return { whitespace: true };
    } else {
      if (value === true || value === false)
        return null;
      if (typeof value !== 'string')
        return null;
      const isWhitespace = value.trim().length === 0;
      return isWhitespace ? { whitespace: true } : null;
    }
  }
}

interface ControlValidation {
  validator: (control: AbstractControl, param?: any) => ValidationErrors | null;
  param?: any;
}
